import React, { useState } from 'react';
import { GenericToast, ToastOrientations, useToast } from '@alucio/lux-ui';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import { useDispatch, useSelector } from 'src/state/redux';
import ActiveUser from 'src/state/global/ActiveUser';
import { togglePageBookmark } from 'src/state/redux/slice/userNotations';
import { getBookmarkedPageIds } from 'src/utils/pageBookmarkHelpers';
import { isCustomDeckORM, isFolderItemORM } from 'src/types/typeguards';

export default function usePageBookmarks() {
  const { activePresentation, setPageNavigationView } = useContent();
  const dispatch = useDispatch();
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const version = activePresentation?.currentPresentablePage.documentVersionORM;
  const pageId = activePresentation?.currentPresentablePage.page.pageId;
  const user = ActiveUser.user;
  const record = useSelector(state => state.userNotations.records
    .filter(item => item.documentVersionId === version?.model.id && item.createdBy === user?.id &&
      item.tenantId === user?.tenantId && item.status === 'ACTIVE')
    .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt)).at(-1));
  const pageIds = getBookmarkedPageIds(record?.notation);
  const orm = activePresentation?.presentable.orm;
  const isCustomDeck = isFolderItemORM(orm) && isCustomDeckORM(orm.relations.itemORM);
  const isSupported = !!version && ['PDF', 'PPTX'].includes(version.model.type) && !isCustomDeck;
  const canEdit = isSupported && !!user && !!version?.meta.version.isLatestPublished &&
    !!version?.meta.permissions.bookmark && !activePresentation?.isHub;

  const toggle = async () => {
    if (!canEdit || !version || !pageId || isSaving) return;
    setIsSaving(true);
    try {
      await dispatch(togglePageBookmark({
        documentId: version.model.documentId, documentVersionId: version.model.id, pageId,
      })).unwrap();
    } catch {
      const message = React.createElement(GenericToast, {
        title: 'Unable to update bookmark. Please try again.', status: 'error',
      });
      toast.add(message,
        ToastOrientations.TOP_RIGHT);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    pageIds,
    isBookmarked: !!pageId && pageIds.includes(pageId),
    isSaving,
    isSupported,
    canEdit,
    toggle,
    view: activePresentation?.pageNavigationView ?? 'all',
    setPageNavigationView,
  };
}
